/**
 * Financial presentation helpers: exact formatting of saved model outputs by
 * their declared semantic type (no floats, missing never becomes zero), the
 * saved result adapter and the identities of the raw GDV passthrough rows.
 */
import { isLosslessNumber } from 'lossless-json'
import { BID_PROFILES, DEAL_MODEL_BID_STATES, type BidEvaluation, type DealModelBidProfile, type DealModelBidState, type DealsV2PropertyModelChoice, type DealsV2ResultDetail, type HomeBuyerBid, type ModelPresentation, type PresentationOutput, type ProfileBid } from '../../../api/financial'
import { HOME_BUYER_MODEL_ID } from '../../../experiences/registry'

export function bidStateLabel(state: DealModelBidState): string {
  const labels: Record<DealModelBidState, string> = {
    computed: 'Calculated', noPassingBid: 'No passing bid', searchLimitReached: 'Search limit reached',
    missingInputs: 'Missing inputs', notApplicable: 'Not applicable', invalidBounds: 'Invalid bounds',
    monotonicityViolation: 'Cannot establish a maximum', evaluationFailed: 'Calculation failed',
    resourceLimit: 'Calculation limit reached', unsupported: 'Not supported',
  }
  return labels[state]
}

// Plain-decimal lexical contract (auction-hub-canonical-json-v1). No floats: formatting must not change a stored value.
const canonicalNumber = /^(?:0|-?[1-9][0-9]*|-?(?:0|[1-9][0-9]*)\.[0-9]*[1-9])$/
const integralNumber = /^(?:0|-?[1-9][0-9]*)$/
const groupDigits = (digits: string): string => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

function incrementDigits(digits: string): string {
  const result = digits.split('')
  for (let index = result.length - 1; index >= 0; index--) {
    if (result[index] !== '9') { result[index] = String.fromCharCode((result[index] ?? '0').charCodeAt(0) + 1); return result.join('') }
    result[index] = '0'
  }
  return `1${result.join('')}`
}

/** Shift an exact decimal, then round display to two places, halves away from zero. */
function percentageDisplay(value: string, shift: number): string {
  const negative = value.startsWith('-')
  const [whole = '', fraction = ''] = (negative ? value.slice(1) : value).split('.')
  const digits = whole + fraction
  const placesToRemove = fraction.length - shift - 2
  let hundredths: string
  if (placesToRemove <= 0) hundredths = digits + '0'.repeat(-placesToRemove)
  else {
    const cut = digits.length - placesToRemove
    hundredths = cut > 0 ? digits.slice(0, cut) : '0'
    if (cut >= 0 && (digits[cut] ?? '0') >= '5') hundredths = incrementDigits(hundredths)
  }
  hundredths = hundredths.replace(/^0+(?=\d)/, '').padStart(3, '0')
  const sign = negative && /[1-9]/.test(hundredths) ? '-' : ''
  return `${sign}${groupDigits(hundredths.slice(0, -2))}.${hundredths.slice(-2)}%`
}

export type OutputScalar = string | boolean | null

/** Format only the declared semantic type; missing values never become zero. */
export function formatOutput(value: OutputScalar | undefined, descriptor: Pick<PresentationOutput, 'semanticType' | 'enumValues' | 'unit'>): string {
  if (value === null || value === undefined) return '—'
  if (descriptor.semanticType === 'boolean') return typeof value === 'boolean' ? (value ? 'Yes' : 'No') : '—'
  if (typeof value !== 'string') return '—'
  if (['enum', 'string', 'date'].includes(descriptor.semanticType)) {
    if (descriptor.semanticType === 'enum' && !descriptor.enumValues.includes(value)) return '—'
    return value
  }
  if (!canonicalNumber.test(value)) return '—'
  if (descriptor.semanticType === 'moneyPence') {
    if (!integralNumber.test(value)) return '—'
    const pence = BigInt(value)
    const absolute = pence < 0n ? -pence : pence
    return `${pence < 0n ? '-' : ''}£${groupDigits((absolute / 100n).toString())}.${(absolute % 100n).toString().padStart(2, '0')}`
  }
  if (descriptor.semanticType === 'money') {
    const negative = value.startsWith('-')
    const [whole = '', fraction = ''] = (negative ? value.slice(1) : value).split('.')
    if (fraction.length > 2) return '—'
    return `${negative ? '-' : ''}£${groupDigits(whole)}.${fraction.padEnd(2, '0')}`
  }
  if (descriptor.semanticType === 'ratio') return percentageDisplay(value, 2)
  if (descriptor.semanticType === 'percentage') return percentageDisplay(value, 0)
  if (['integer', 'count', 'months', 'days'].includes(descriptor.semanticType) && !integralNumber.test(value)) return '—'
  if (['count', 'months', 'days', 'areaSqm'].includes(descriptor.semanticType) && value.startsWith('-')) return '—'
  return descriptor.unit ? `${value} ${descriptor.unit}` : value
}

export function formatBidPence(value: string | null): string {
  return formatOutput(value, { semanticType: 'moneyPence', unit: 'pence', enumValues: [] })
}

/** The saved value at a JSON pointer, exact (lossless numbers become their decimal text). */
export function valueAt(root: unknown, pointer: string): OutputScalar | undefined {
  let value: unknown = root
  for (const part of pointer.slice(1).split('/')) value = value && typeof value === 'object' ? (value as Record<string, unknown>)[part.replace(/~1/g, '/').replace(/~0/g, '~')] : undefined
  return isLosslessNumber(value) ? value.toString() : typeof value === 'string' || typeof value === 'boolean' || value === null ? value : undefined
}

export function humanize(value: string | null | undefined): string {
  if (!value) return 'Unknown'
  return value.replaceAll('_', ' ').replace(/\b\w/g, character => character.toUpperCase())
}

/* ---------- saved result adapter ---------- */

export interface FinancialModelResult {
  calculationState: 'computed' | 'notComputable' | 'evaluationFailed'
  readinessState: 'ready' | 'readyWithWarnings' | 'missingInputs' | 'ineligible' | 'notApplicable' | 'failed'
  result: Record<string, unknown> | null
  /** Listing-price scenario retained only for the explicit comparison ledger. */
  calculationPriceResult?: Record<string, unknown> | null
  presentation: ModelPresentation
  calculatedAt: string
  effectiveAsOf?: string
  selectedRefurbishmentProfile?: DealModelBidProfile
  bidEvaluation?: BidEvaluation
  homeBuyerBidEvaluation?: HomeBuyerBid
}

/** All auction figures use one coherent maximum-bid scenario; an unavailable maximum has no figures. */
export function maximumBidResult(result: FinancialModelResult): FinancialModelResult {
  const bid = result.bidEvaluation?.bid
  if (!bid) return result
  return {
    ...result,
    calculationPriceResult: result.calculationPriceResult === undefined ? result.result : result.calculationPriceResult,
    result: bid.state === 'computed' && bid.maxBidPence !== null ? bid.atMaximum : null,
  }
}

type JsonRecord = Record<string, unknown>
const record = (value: unknown): JsonRecord | null => value !== null && typeof value === 'object' && !Array.isArray(value) && !isLosslessNumber(value) ? value as JsonRecord : null
const exactString = (value: unknown): string | null => value === null || value === undefined ? null : typeof value === 'string' || typeof value === 'number' || isLosslessNumber(value) ? String(value) : null
const bidState = (value: unknown): DealModelBidState | null => typeof value === 'string' && DEAL_MODEL_BID_STATES.some(state => state === value) ? value as DealModelBidState : null

function bidEvaluation(output: JsonRecord): BidEvaluation | null {
  const evaluation = record(output.bidEvaluation)
  const bid = record(evaluation?.bid)
  const proof = record(bid?.proof)
  const profile = evaluation?.profile
  const state = bidState(bid?.state)
  if (!bid || !proof || !state || !BID_PROFILES.some(item => item === profile)) return null
  return {
    profile: profile as DealModelBidProfile,
    bid: { state, maxBidPence: exactString(bid.maxBidPence), limitingConstraint: typeof bid.limitingConstraint === 'string' ? bid.limitingConstraint : null, passesAtMarketedPrice: typeof bid.passesAtMarketedPrice === 'boolean' ? bid.passesAtMarketedPrice : null, atMaximum: record(bid.atMaximum), proof: proof as ProfileBid['proof'] },
  }
}

function homeBuyerBidEvaluation(output: JsonRecord): HomeBuyerBid | null {
  const bid = record(output.homeBuyerBidEvaluation) ?? record(record(output.bidEvaluation)?.bid)
  const state = bidState(bid?.state)
  if (!bid || !state) return null
  return { state, maxBidPence: exactString(bid.maxBidPence), passesAtMarketedPrice: typeof bid.passesAtMarketedPrice === 'boolean' ? bid.passesAtMarketedPrice : null, limitingConstraint: typeof bid.limitingConstraint === 'string' ? bid.limitingConstraint : null }
}

/** The saved result owns every calculated value; the presentation only supplies labels and formats. */
export function financialResult(model: DealsV2PropertyModelChoice, detail: DealsV2ResultDetail): FinancialModelResult {
  const output = record(detail.output)
  const isHomeBuyer = model.logicalModelId === HOME_BUYER_MODEL_ID
  const bid = output ? bidEvaluation(output) : null
  const personalBid = output && isHomeBuyer ? homeBuyerBidEvaluation(output) : null
  const diagnostics = Array.isArray(detail.diagnostics) ? detail.diagnostics : []
  const missingInputs = diagnostics.some(item => record(item)?.code === 'MISSING_REQUIRED_INPUT')
  const hasWarnings = diagnostics.some(item => record(item)?.severity === 'warning')
  return {
    calculationState: detail.state,
    readinessState: detail.state === 'computed' ? (hasWarnings ? 'readyWithWarnings' : 'ready') : detail.state === 'notComputable' ? (missingInputs ? 'missingInputs' : 'notApplicable') : 'failed',
    result: output,
    presentation: model.presentation,
    calculatedAt: detail.calculatedAt,
    effectiveAsOf: detail.effectiveAsOf,
    ...(bid ? { bidEvaluation: bid, selectedRefurbishmentProfile: bid.profile } : {}),
    ...(personalBid ? { homeBuyerBidEvaluation: personalBid } : {}),
  }
}

/* ---------- personal GDV rows ---------- */

// Exact authoring identities of raw GDV passthrough rows. Derived money outputs
// and similarly named custom rows do not become editable property valuations.
const GDV_OUTPUT_IDS: Record<string, string> = {
  [HOME_BUYER_MODEL_ID]: 'e1220000-0000-4000-8000-000000000002',
  '370f437f-96fa-4754-86bd-f59ee80bf60f': '0510dfae-12fe-50a9-acef-a032761dd047',
  'b44479f1-e327-4821-8492-6054d1e3c93c': 'e9d964d8-0ef5-5fc7-9d42-31041c3cbbac',
}

export function isPersonalGdvOutput(modelId: string, outputId: string): boolean { return GDV_OUTPUT_IDS[modelId] === outputId }

/** Published money outputs use exact decimal pounds, including trailing zeros. */
export function gdvOutputPence(value: unknown): number | null {
  if (typeof value !== 'string') return null
  const match = /^(\d+)(?:\.(\d+))?$/.exec(value)
  if (!match || /[1-9]/.test((match[2] ?? '').slice(2))) return null
  const amount = BigInt(match[1] ?? '0') * 100n + BigInt((match[2] ?? '').padEnd(2, '0').slice(0, 2))
  return amount > 0n && amount <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(amount) : null
}

export const numberText = (value: number | null | undefined) => value === null || value === undefined ? '—' : new Intl.NumberFormat('en-GB').format(value)
/** Presentation alias only (the workbench's neutralListingLabel); source identifiers keep their values. */
export function neutral(value: string): string {
  if (/^(?:rightmoveMarket|propertyMarket)\.askingRent$/i.test(value)) return 'Local advertised rents'
  if (/^rightmoveMarket$/i.test(value)) return 'Local market'
  return value.replace(/rightmove(?:\.co\.uk|_co_uk)?/gi, 'Local listings')
}
